import { useState } from '#imports'
import { computed } from 'vue'

import type { ProductCardProduct } from '../components/ProductCard/ProductCard.types'
import {
  createBackboxObjectId,
  productToProductCard,
} from '../helpers/recommendationHelper'

import { useAlgoliaRecommendations } from './useAlgoliaRecommendations/useAlgoliaRecommendations'

interface AddToCartPopoverState {
  isVisible: boolean
  product: ProductCardProduct | null
  hasError: boolean
  errorBlocking: boolean
  productAlgoliaObjectId: string
  recommendations: ProductCardProduct[]
  sessionId: number
}

const MINIMUM_DISPLAY_DURATION_MS = 500

function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(undefined)
    }, ms)
  })
}

export function useAddToCartPopover() {
  const state = useState<AddToCartPopoverState>('add-to-cart-popover', () => ({
    isVisible: false,
    product: null,
    hasError: false,
    errorBlocking: true,
    productAlgoliaObjectId: '',
    recommendations: [],
    sessionId: 0,
  }))

  const productAlgoliaObjectIds = computed(() =>
    state.value.productAlgoliaObjectId
      ? [state.value.productAlgoliaObjectId]
      : [],
  )

  const { data: recommendationsRaw, refresh: refreshRecommendations } =
    useAlgoliaRecommendations(productAlgoliaObjectIds, 'bought-together', {
      maxRecommendations: 2,
      queryParameters: {
        ruleContexts: ['only_accessory', 'only_compatible'],
      },
    })

  function getProductAlgoliaObjectId(product: ProductCardProduct) {
    if (!product.grade?.value) return product.rawProduct.id

    return createBackboxObjectId(
      product.rawProduct.id,
      product.grade.value,
      product.specialOfferType ?? 0,
    )
  }

  async function show(product: ProductCardProduct) {
    state.value.sessionId += 1
    const currentSessionId = state.value.sessionId

    state.value.product = product
    state.value.isVisible = true
    state.value.hasError = false
    state.value.recommendations = []
    state.value.productAlgoliaObjectId = getProductAlgoliaObjectId(product)

    await Promise.all([
      refreshRecommendations(),
      delay(MINIMUM_DISPLAY_DURATION_MS),
    ])

    if (state.value.sessionId !== currentSessionId) return

    state.value.recommendations =
      recommendationsRaw.value?.map(productToProductCard) ?? []
  }

  function showError(product?: ProductCardProduct, blocking = true) {
    state.value.errorBlocking = blocking
    state.value.product = blocking ? null : (product ?? state.value.product)
    state.value.isVisible = true
    state.value.hasError = true
  }

  function hide() {
    state.value.sessionId += 1
    state.value.isVisible = false
    state.value.hasError = false
  }

  return {
    isVisible: computed(() => state.value.isVisible),
    product: computed(() => state.value.product),
    hasError: computed(() => state.value.hasError),
    errorBlocking: computed(() => state.value.errorBlocking),
    recommendations: computed(() => state.value.recommendations),
    show,
    showError,
    hide,
  }
}
